<template>
  <div class="market-data-page">
    <ContextFiles
      :files="files"
      :uploading-files="uploadingFiles"
      :max-files="MAX_FILES"
      :can-edit="canEdit"
      :disabled="isBusy"
      :accept="MARKET_DATA_ACCEPT"
      @upload="onUpload"
      @remove="onRemove"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import ContextFiles from "@components/ContextFiles/ContextFiles.vue";
import {
  getMarketDataFiles,
  uploadMarketDataFiles,
  deleteMarketDataFile,
  mapMarketDataFileToUi,
} from "@/api/marketDataFilesAPI";
import { normalizeAdminPermission } from "@/utils/defaultLoggedInRedirect";

const MAX_FILES = 20;
const MARKET_DATA_ACCEPT =
  ".pdf,.docx,.xlsx,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

const files = ref([]);
const uploadingFiles = ref([]);
const isBusy = ref(false);

const instance = getCurrentInstance();

const accountId = computed(() => {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
});

const canEdit = computed(() => {
  try {
    const accessPermission = JSON.parse(localStorage.getItem("accessPermission") || "{}");
    return normalizeAdminPermission(accessPermission).hasElevatedNav;
  } catch {
    return false;
  }
});

function toastError(message) {
  const toast = instance?.proxy?.$toast;
  if (toast && typeof toast.error === "function") {
    toast.error(message);
  } else {
    console.error(message);
  }
}

function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.detail) return String(data.detail);
  if (Array.isArray(data) && data[0]) return String(data[0]);
  if (data && typeof data === "object") {
    const first = Object.values(data)[0];
    if (Array.isArray(first) && first[0]) return String(first[0]);
    if (typeof first === "string") return first;
  }
  return fallback;
}

async function loadFiles() {
  if (!accountId.value) return;
  try {
    const { data } = await getMarketDataFiles(accountId.value);
    const list = Array.isArray(data) ? data : data?.results || [];
    files.value = list.map(mapMarketDataFileToUi);
  } catch (error) {
    console.error("Error loading market data files:", error);
    toastError(apiErrorMessage(error, "Failed to load market data files."));
  }
}

async function onUpload(fileList) {
  if (!canEdit.value || !fileList?.length || !accountId.value) return;

  uploadingFiles.value = Array.from(fileList).map((file) => ({
    name: file.name,
    progress: 0,
  }));

  try {
    isBusy.value = true;
    await uploadMarketDataFiles(accountId.value, fileList, (event) => {
      if (!event.total) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      uploadingFiles.value = uploadingFiles.value.map((file) => ({
        ...file,
        progress,
      }));
    });
    await loadFiles();
  } catch (error) {
    console.error("Error uploading market data file:", error);
    toastError(apiErrorMessage(error, "Failed to upload market data file."));
  } finally {
    isBusy.value = false;
    uploadingFiles.value = [];
  }
}

async function onRemove(fileId) {
  if (!canEdit.value || !fileId || !accountId.value) return;

  try {
    isBusy.value = true;
    await deleteMarketDataFile(accountId.value, fileId);
    await loadFiles();
  } catch (error) {
    console.error("Error removing market data file:", error);
    toastError(apiErrorMessage(error, "Failed to remove market data file."));
  } finally {
    isBusy.value = false;
  }
}

onMounted(() => {
  void loadFiles();
});
</script>

<style scoped>
.market-data-page {
  width: 100%;
  padding: 8px 0 24px;
}
</style>
